import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { findStarter, Slot, SLOT_ALLOWED_MODULES } from './manifest';
import { fetchGraphql } from '../../aicrowd-ui/graphql/fetch';
import { parseQuery, resolveVariables, interpretResult } from './livePreview';
import { useVarValues } from './varValuesContext';
import PreviewPane from './PreviewPane';
import CodePane from './CodePane';
import DataInputs from './DataInputs';

function slotOf(slug?: string): Slot | null {
  if (!slug) return null;
  const slot = (Object.keys(SLOT_ALLOWED_MODULES) as Slot[])
    .find((s) => s.toLowerCase() === slug.toLowerCase());
  return slot || null;
}

export default function StarterDetail() {
  const { slot: slotSlug, pattern } = useParams<{ slot: string; pattern: string }>();
  const slot = slotOf(slotSlug);
  const entry = slot && pattern ? findStarter(slot, pattern) : null;
  const original = entry ? entry.source : '';

  // Hooks run unconditionally (before the early returns).
  const { values } = useVarValues();
  const [code, setCode] = useState(original);
  const [rendered, setRendered] = useState<{ code: string; data?: unknown }>({ code: original, data: undefined });
  const [status, setStatus] = useState('Showing FAKE_DATA');
  const [loading, setLoading] = useState(false);
  // Monotonic token: a fetch only applies if it's still the latest request and
  // the starter hasn't changed since it began (prevents stale-result clobber).
  const requestId = useRef(0);

  // Reset to FAKE_DATA preview when switching starters.
  useEffect(() => {
    requestId.current += 1;
    setCode(original);
    setRendered({ code: original, data: undefined });
    setStatus('Showing FAKE_DATA');
  }, [original]);

  const variables = useMemo(() => parseQuery(code).variables, [code]);
  const dirty = code !== rendered.code;

  const handlePreview = async () => {
    const { query, variables: vnames } = parseQuery(code);
    const { missing, vars } = resolveVariables(vnames, values);
    if (!query || vnames.length === 0 || missing.length > 0) {
      setRendered({ code, data: undefined });
      setStatus(missing.length ? `Showing FAKE_DATA — fill: ${missing.join(', ')}` : 'Showing FAKE_DATA');
      return;
    }
    const reqId = (requestId.current += 1);
    const capturedCode = code;
    setLoading(true);
    setStatus('Loading live data…');
    try {
      const res = await fetchGraphql({ query, variables: vars });
      if (reqId !== requestId.current) return; // superseded by a newer Preview or a starter switch
      const { data, status: s } = interpretResult(res, vars.slug ?? '…');
      setRendered({ code: capturedCode, data });
      setStatus(s);
    } catch (e) {
      if (reqId !== requestId.current) return;
      setRendered({ code: capturedCode, data: undefined });
      setStatus(`Showing FAKE_DATA — ${(e as Error).message}`);
    } finally {
      if (reqId === requestId.current) setLoading(false);
    }
  };

  if (!slot || !pattern) {
    return <div style={{ padding: '2rem' }}>Select a starter from the sidebar.</div>;
  }
  if (!entry) {
    return <div style={{ padding: '2rem' }}>Starter "{slotSlug}/{pattern}" not found.</div>;
  }

  return (
    <div style={{ display: 'flex', gap: '1rem', padding: '1.5rem', height: '100%', boxSizing: 'border-box' }}>
      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: '1rem', overflowY: 'auto', overscrollBehavior: 'contain' }}>
        <h1 style={{ margin: 0, fontSize: '1.5rem' }}>{entry.pattern.replace(/^\d{2}_/, '')}</h1>
        <div style={{ fontSize: 13, color: '#718096' }}>
          Slot: <strong>{entry.slot}</strong> · Allowed modules: <code>{SLOT_ALLOWED_MODULES[slot].join(', ')}</code>
        </div>
        <DataInputs variables={variables} loading={loading} status={status} dirty={dirty} onPreview={handlePreview} />
        <PreviewPane source={rendered.code} data={rendered.data} allowedModules={SLOT_ALLOWED_MODULES[slot]} />
      </div>
      <div style={{ flex: 1, minHeight: 0, height: '100%' }}>
        <CodePane entry={entry} code={code} onChange={setCode} onReset={() => setCode(original)} />
      </div>
    </div>
  );
}
